import { useState } from 'react';
import { AlertTriangleIcon } from 'lucide-react';

import { PRODUCT_CAROUSEL_TYPES, SLICE_REGISTRY } from 'components/storefront/utils/constants';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

/**
 * Product carousel display types that require an image before publish.
 *
 * @constant {string[]}
 */
const PRODUCT_CAROUSEL_TYPES_REQUIRING_IMAGE = Object.freeze([
    SLICE_REGISTRY.PRODUCT_CAROUSEL_2.displayType,
    SLICE_REGISTRY.PRODUCT_CAROUSEL_3.displayType,
]);

/**
 * Dialog for switching a product carousel slice to a different display type.
 *
 * @param {Object} props
 * @param {string} props.currentDisplayType
 * @param {string} [props.imageUrl]
 * @param {Function} props.onConfirm
 * @param {Function} props.onOpenChange
 * @param {boolean} props.open
 */
function SwitchSliceTypeDialog({ currentDisplayType, imageUrl, onConfirm, onOpenChange, open }) {
    const [selectedType, setSelectedType] = useState('');

    const alternateTypes = PRODUCT_CAROUSEL_TYPES.filter((type) => type !== currentDisplayType);
    const showImageWarning = PRODUCT_CAROUSEL_TYPES_REQUIRING_IMAGE.includes(selectedType) && !imageUrl;

    const handleOpenChange = (nextOpen) => {
        if (!nextOpen) {
            setSelectedType('');
        }
        onOpenChange(nextOpen);
    };

    const handleConfirm = () => {
        if (!selectedType) return;
        onConfirm(selectedType);
        setSelectedType('');
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Switch slice type</DialogTitle>
                    <DialogDescription>
                        Change this carousel&apos;s slice type. Existing content will be kept.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    <Label htmlFor="switch-slice-type">New type</Label>
                    <Select value={selectedType} onValueChange={setSelectedType}>
                        <SelectTrigger id="switch-slice-type" className="w-full">
                            <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                        <SelectContent>
                            {alternateTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                    {SLICE_REGISTRY[type].displayName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {showImageWarning && (
                        <div className="flex items-center gap-2 mt-1">
                            <AlertTriangleIcon className="h-4 w-4 text-[#dea455]" />
                            <p className="text-sm text-[#dea455]">
                                This type requires an image before publish. Add one after switching.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button disabled={!selectedType} onClick={handleConfirm}>
                        Switch
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SwitchSliceTypeDialog;
